<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import MessageSentAnimation from '@/components/Sourcing/MessageSentAnimation.vue';
import { nextTick } from 'vue';
import { MessagingService } from '@/core/sourcing/messaging/messaging.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import Autocomplete from '@/components/Shared/Input/Autocomplete/Autocomplete.vue';
import { pluralize } from '@/utils/strings/pluralize.util';
import { type Project } from '@factoryfixinc/ats-interfaces';
import { InternalError } from '@/core/shared/errors/internal.error';
import Checkbox from '../Input/Checkbox/Checkbox.vue';
import Divider from '../Divider/Divider.vue';
import BaseModal from '../Modals/BaseModal.vue';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { useProjectSelector } from '@/composables/useProjectSelector';

const dialogService = new DialogService();
const messagingService = new MessagingService();

const emit = defineEmits<{
  (e: 'submit', value: Project): void;
  (e: 'close', value: { messageSent: boolean }): void;
  (e: 'remove-pro', value: number): void;
}>();

const message = ref('');
const messageSentFlag = ref(false);
const showMore = ref(false);
const sendingMessage = ref(false);
const selectedJobId = ref<number | undefined>();
const {
  projectSearch,
  myJobsFilter,
  copilotJobsFilter,
  atsJobsFilter,
  projects,
  isLoadingProjects,
  fetchProjects,
  renderProject,
  resetFilters,
} = useProjectSelector({ getExcludedJobIds: () => modalProps.value.excludedJobIds });

const modalProps = computed(() => dialogService.addProToJobModalProps);
const isOpen = computed(() => modalProps.value.isOpen);

const disableSendButton = computed(() => {
  return !selectedJobId.value || !recipientsCount.value;
});

const recipientsCount = computed(() => modalProps.value.pros?.length);

const sendMessage = async () => {
  try {
    sendingMessage.value = true;
    const project = projects.value.find((project) => project.jobId === selectedJobId.value);

    if (!project) {
      throw new Error('Project is not defined');
    }

    if (!modalProps.value.pros) {
      throw new Error('Pros are not defined');
    }

    await messagingService.sendMessageToRecipients(message.value, project, modalProps.value.pros);

    if (modalProps.value.additionalActionsBeforeSubmit) {
      await modalProps.value.additionalActionsBeforeSubmit(project);
    }

    messageSentFlag.value = true;
    emit('submit', project);
  } catch (error) {
    ErrorService.captureException(error);

    if (!(error instanceof InternalError)) {
      SnackbarService.critical('Something went wrong. Please try again.');
    }
  } finally {
    sendingMessage.value = false;
  }
};

async function close({ messageSent }: { messageSent: boolean }) {
  emit('close', { messageSent });
  await nextTick();
  resetForm();

  // Wait for close animation to finish
  setTimeout(() => {
    messageSentFlag.value = false;
  }, 500);
}

function resetForm() {
  selectedJobId.value = undefined;
  message.value = '';
  resetFilters();
}

watch(isOpen, async (open) => {
  if (open) {
    if (recipientsCount.value === 1) {
      showMore.value = true;
    }

    await fetchProjects();
  }
});
</script>

<template>
  <BaseModal
    :model-value="isOpen"
    :is-loading="sendingMessage"
    :is-submit-disabled="disableSendButton"
    :show-close-button="true"
    :show-cancel-button="true"
    :show-heading="!messageSentFlag"
    :show-actions="!messageSentFlag"
    :title="`Add ${pluralize(modalProps.pros?.length, 'candidate')} to job`"
    submit-button-text="Add to job"
    @submit="sendMessage"
    @cancel="close({ messageSent: false })"
  >
    <MessageSentAnimation
      v-if="messageSentFlag"
      @animation:complete="close({ messageSent: true })"
    />

    <div v-else>
      <!-- Job Selector -->
      <div class="mt-5">
        <span class="text-sm font-bold">Select job*</span>
        <div class="mt-1">
          <Autocomplete
            v-model="selectedJobId"
            v-model:search="projectSearch"
            :disabled="sendingMessage"
            :loading="isLoadingProjects"
            :items="projects"
            :plain="true"
            :render-item="renderProject"
            clearable
            item-title="title"
            item-value="jobId"
            placeholder="Select a job"
          >
            <template #prepend-item>
              <div class="flex gap-6 px-4 pb-4 pt-2">
                <span class="text-xs text-shade-800">Filter by:</span>

                <div class="flex items-center gap-2">
                  <Checkbox v-model="myJobsFilter"></Checkbox>
                  <span class="text-xs text-shade-800">My jobs</span>
                </div>

                <div class="flex items-center gap-2">
                  <Checkbox v-model="copilotJobsFilter"></Checkbox>
                  <span class="text-xs text-shade-800">Active jobs</span>
                </div>

                <div class="flex items-center gap-2">
                  <Checkbox v-model="atsJobsFilter"></Checkbox>
                  <span class="text-xs text-shade-800">ATS jobs</span>
                </div>
              </div>

              <Divider class="mb-1" />
            </template>
          </Autocomplete>
        </div>
      </div>

      <!-- Textarea -->
      <div v-if="modalProps.showMessageField" class="mt-5">
        <span class="text-sm font-bold">Send first message</span>
        <span class="text-sm text-[#979797]">&nbsp;(optional)</span>
        <textarea
          v-model="message"
          :disabled="sendingMessage"
          class="mt-1 w-full resize-none rounded-md border border-tint-80 px-4 py-4 text-shade-900 placeholder:text-shade-800 focus:outline-none focus:ring-2 focus:ring-highlight-500"
          placeholder="Type your message here"
          rows="4"
        />
      </div>

      <div class="mt-5"></div>
    </div>
  </BaseModal>
</template>
